// src/components/dialog/DlgRegistrationForm.tsx

import {
  EmailTwoTone,
  FaceTwoTone,
  VisibilityOffTwoTone,
  VisibilityTwoTone,
} from '@mui/icons-material'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { KTextField } from '..'
import { isValidEmail, isValidPassword } from '../../api'
import { useUserApi } from '../../hooks'

const onDoNothing = event => event.preventDefault()

type Props = {
  open: boolean
}

export const DlgRegistrationForm = ({ open }: Props) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const { register } = useUserApi()

  const [regForm, setRegForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
  })
  const [termsChecked, setTermsChecked] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const onTogglePasswordMode = () => setShowPassword(!showPassword)

  const LICENSE_AGREEMENT_AND_PRIVACY_TEXT = (
    <small style={{ textTransform: 'uppercase', color: theme.palette.white }}>
      ich stimme{' '}
      <a
        target={'_blank'}
        rel={'noopener noreferrer'}
        href={'https://karma.de/agb'}
        style={{ color: theme.palette.white, fontWeight: 'bold' }}
      >
        AGB
      </a>{' '}
      und{' '}
      <a
        target={'_blank'}
        rel={'noopener noreferrer'}
        href={'https://karma.de/datenschutz'}
        style={{ color: theme.palette.white, fontWeight: 'bold' }}
      >
        Datenschutzerklärung
      </a>{' '}
      zu
    </small>
  )

  const INVALID_EMAIL_TEXT = 'E-Mail Adresse muss ein gültiges Schema besitzen.'
  const INVALID_PASSWORD_TEXT =
    'Passwort muss mind. 8 Stellen haben, 1 Groß-, 1 Kleinbuchstaben und 1 Zahl enthalten.'

  const IS_INVALID_REGDATA =
    regForm.firstName === '' ||
    regForm.lastName === '' ||
    regForm.email === '' ||
    regForm.password === '' ||
    !isValidEmail(regForm.email) ||
    !isValidPassword(regForm.password) ||
    !termsChecked

  const changeFirstName = event => {
    const firstName = event.target.value

    setRegForm(prev => ({ ...prev, firstName }))
  }

  const changeLastName = event => {
    const lastName = event.target.value

    setRegForm(prev => ({ ...prev, lastName }))
  }

  const changeEmail = event => {
    const email = event.target.value.toLowerCase()

    setRegForm(prev => ({ ...prev, email }))
  }

  const changePassword = event => {
    const password = event.target.value

    setRegForm(prev => ({ ...prev, password }))
  }

  const submitRegistration = async event => {
    event.preventDefault()

    await register(regForm, undefined)
    navigate('/pages/register-page/confirm')
  }

  return (
    <Dialog open={open} sx={{ zIndex: 3 }}>
      <DialogTitle>Registrieren</DialogTitle>

      <DialogContent sx={{ p: 1, mt: 1 }}>
        <KTextField
          autoComplete={'first-name'}
          helperText={
            regForm.firstName === '' && 'Der Vorname darf nicht leer sein.'
          }
          icon={<FaceTwoTone />}
          label={'Vorname'}
          onChange={changeFirstName}
          required
          value={regForm.firstName}
        />

        <KTextField
          autoComplete={'last-name'}
          helperText={
            regForm.lastName === '' && 'Der Name darf nicht leer sein.'
          }
          icon={<FaceTwoTone />}
          label={'Name'}
          onChange={changeLastName}
          required
          value={regForm.lastName}
        />

        <KTextField
          autoComplete={'email'}
          helperText={!isValidEmail(regForm.email) && INVALID_EMAIL_TEXT}
          icon={<EmailTwoTone />}
          label={'E-Mail'}
          onChange={changeEmail}
          required
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position={'end'}>
                  <IconButton tabIndex={-1}>
                    <EmailTwoTone />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          value={regForm.email}
        />

        <FormControl sx={{ mt: 2 }} fullWidth>
          <TextField
            autoComplete={'current-password'}
            helperText={
              !isValidPassword(regForm.password) && INVALID_PASSWORD_TEXT
            }
            label={'Passwort'}
            onChange={changePassword}
            required
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position={'end'}>
                    <IconButton
                      onClick={onTogglePasswordMode}
                      onMouseDown={onDoNothing}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <VisibilityTwoTone />
                      ) : (
                        <VisibilityOffTwoTone />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            type={showPassword ? 'text' : 'password'}
            value={regForm.password}
          />
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={termsChecked}
              color={'secondary'}
              onClick={() => setTermsChecked(!termsChecked)}
              required
            />
          }
          label={LICENSE_AGREEMENT_AND_PRIVACY_TEXT}
        />

        <form name={'form'} onSubmit={submitRegistration}>
          <Button disabled={IS_INVALID_REGDATA} type={'submit'}>
            Registrieren
          </Button>
        </form>
      </DialogActions>
    </Dialog>
  )
}
